// hooks/auth/useAuthCredentials.ts
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectFieldErrors,
} from '@/store/selectors/auth';
import { credentialVerification } from '@/store/thunks/auth/credentialVerificationThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { authenticateUser } from '@/store/thunks/poc/auth/authThunks';

/**
 * Hook for /login route
 * Handles username/password credential submission and navigation to 2FA selection
 *
 * @returns {Object} Hook state and handlers
 * @returns {Object} return.state - Current authentication state
 * @returns {boolean} return.state.isLoading - Loading indicator during submission
 * @returns {string} [return.state.error] - Global error message if authentication fails
 * @returns {Object} return.state.fieldErrors - Field-level validation errors (username, password)
 * @returns {Object} return.handlers - Action handlers
 * @returns {Function} return.handlers.submit - Submit credentials (username, password)
 *
 * @example
 * const { state, handlers } = useAuthCredentials();
 *
 * return (
 *   <LoginForm
 *     onSubmit={handlers.submit}
 *     loading={state.isLoading}
 *     error={state.error}
 *     fieldErrors={state.fieldErrors}
 *   />
 * );
 */
export function useAuthCredentials() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const fieldErrors = useSelector(selectFieldErrors);
  const isLoading = authStatus === 'loading';

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const submit = async(credentials: { username: string; password: string }) => {
    await dispatch(authenticateUser({
      email: credentials.username,
      password: credentials.password,
      onSuccess: () => { /* empty */ },
    }));
    const action = await dispatch(credentialVerification(credentials));
    if (credentialVerification.fulfilled.match(action)) {
      await navigate({ to: '/login/2fa/select' });
    }
  };

  return {
    state: { isLoading, error, fieldErrors },
    handlers: { submit },
  };
}
